import React, { useState, useEffect } from 'react';
import { FiSearch, FiRefreshCw, FiFilter, FiChevronDown, FiChevronUp, FiEye, FiX, FiDownload, FiUsers, FiPhone, FiCalendar, FiBook, FiEdit2, FiCheck } from 'react-icons/fi';
import { BiSortAlt2 } from 'react-icons/bi';
import { BsCheckCircleFill, BsXCircleFill } from 'react-icons/bs';
import { exportToCSV } from '../../utils/csvExport';
import { fetchWithAuth } from '../../utils/api';
import moment from 'moment';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

const UserDetailTable = () => {
  // State variables
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter states - Simplified
  const [showFilters, setShowFilters] = useState(false);

  // Export state
  const [exportLoading, setExportLoading] = useState(false);

  // Selected user details modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false);
  const [userDetailsWithStats, setUserDetailsWithStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Phone number edit state
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [phoneUpdateLoading, setPhoneUpdateLoading] = useState(false);

  // User booking history modal state
  const [bookingHistoryUser, setBookingHistoryUser] = useState(null);
  const [bookingHistoryOpen, setBookingHistoryOpen] = useState(false);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [bookingHistoryLoading, setBookingHistoryLoading] = useState(false);
  const [bookingHistoryPagination, setBookingHistoryPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 0
  });

  // Fetch users based on filters and pagination
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy: sortField,
        sortOrder: sortOrder
      };

      // Add filters if they exist
      if (search) params.search = search;

      // Make API call using fetchWithAuth utility (handles auth and 401 automatically)
      const url = `${API_BASE_URL}/api/admin/user-listing`;
      const urlWithParams = new URL(url);
      urlWithParams.search = new URLSearchParams(params).toString();

      const response = await fetchWithAuth(urlWithParams, {
        method: 'GET'
      });
      
      const data = await response.json();
      
      // Update state with response data
      if (data.success) {
        setUsers(data.data.users);
        setTotalUsers(data.data.pagination.totalUsers || data.data.users.length);
        setTotalPages(data.data.pagination.totalPages);
      } else {
        console.error('Error fetching users:', data.message);
        alert('Failed to fetch users data');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // 401 errors are handled automatically by fetchWithAuth
      if (!error.message.includes('Session expired')) {
        alert('Failed to fetch users data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel function
  const exportToExcel = async () => {
    setExportLoading(true);
    
    try {
      // If we need all users (not just current page), fetch them
      let allUsers = users;
      
      // If you want to export all users with current filters, make API call
      if (totalUsers > users.length) {
        const token = localStorage.getItem('token');
        
        if (!token) {
          alert('Authentication token not found. Please log in again.');
          setExportLoading(false);
          return;
        }

        // Build query parameters for all users
        const params = {
          page: 1,
          limit: totalUsers, // Get all users
          sortBy: sortField,
          sortOrder: sortOrder
        };

        // Add filters if they exist
        if (search) params.search = search;

        const url = new URL(`${API_BASE_URL}/api/admin/user-listing`);
        url.search = new URLSearchParams(params).toString();

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        allUsers = data.data.users;
      }

      // Transform user data for Excel export (simplified fields)
      const exportData = allUsers.map((user, index) => ({
        'S.No': index + 1,
        'User ID': user._id,
        'Name': user.name || '',
        'Phone Number': user.phoneNumber || '',
        'Email': user.email || '',
        'Created At': user.createdAt ? new Date(user.createdAt).toLocaleString() : '',
        'Updated At': user.updatedAt ? new Date(user.updatedAt).toLocaleString() : ''
      }));

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Users_Export_${currentDate}.csv`;

      // Export to CSV
      exportToCSV(exportData, filename);

      // Show success message
      alert(`${allUsers.length} users exported successfully!`);

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export users data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // View user details with stats
  const viewUserDetails = async (user) => {
    setSelectedUser(user);
    setUserDetailsModalOpen(true);
    setStatsLoading(true);
    setUserDetailsWithStats(null);
    
    try {
      // Fetch detailed user stats
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication token not found. Please log in again.');
        return;
      }

      // Make API call with userId query parameter to get detailed stats
      const url = new URL(`${API_BASE_URL}/api/admin/user-listing`);
      url.searchParams.append('userId', user._id);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUserDetailsWithStats(data.data);
      } else {
        console.error('Error fetching user details:', data.message);
        alert('Failed to fetch user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      alert('Failed to fetch user details');
    } finally {
      setStatsLoading(false);
    }
  };

  // Close user details modal
  const closeUserDetailsModal = () => {
    setUserDetailsModalOpen(false);
    setSelectedUser(null);
    setUserDetailsWithStats(null);
    setEditingPhone(false);
    setNewPhoneNumber('');
  };

  // Update user phone number
  const handleUpdatePhoneNumber = async () => {
    if (!newPhoneNumber.trim()) {
      alert('Please enter a phone number');
      return;
    }

    if (newPhoneNumber.trim() === selectedUser.phoneNumber) {
      setEditingPhone(false);
      return;
    }

    setPhoneUpdateLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/admin/user/${selectedUser._id}/phone-number`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phoneNumber: newPhoneNumber.trim() })
        }
      );

      const data = await response.json();

      if (data.success) {
        // Update the selected user in state
        setSelectedUser(prev => ({ ...prev, phoneNumber: data.data.phoneNumber }));
        // Update the user in the list as well
        setUsers(prev => prev.map(u =>
          u._id === selectedUser._id ? { ...u, phoneNumber: data.data.phoneNumber } : u
        ));
        setEditingPhone(false);
        alert('Phone number updated successfully');
      } else {
        alert(data.message || 'Failed to update phone number');
      }
    } catch (error) {
      console.error('Error updating phone number:', error);
      if (!error.message.includes('Session expired')) {
        alert('Failed to update phone number');
      }
    } finally {
      setPhoneUpdateLoading(false);
    }
  };

  // Fetch booking history for a user
  const fetchUserBookingHistory = async (user, page = 1) => {
    setBookingHistoryUser(user);
    setBookingHistoryOpen(true);
    setBookingHistoryLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication token not found. Please log in again.');
        return;
      }

      const params = new URLSearchParams({
        userId: user._id,
        page: page,
        limit: 10
      });

      const response = await fetch(
        `${API_BASE_URL}/api/admin/appointments?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();

      if (data.success) {
        setBookingHistory(data.data.appointments);
        setBookingHistoryPagination({
          page: data.data.pagination.currentPage,
          limit: data.data.pagination.limit,
          total: data.data.pagination.totalAppointments,
          totalPages: data.data.pagination.totalPages
        });
      } else {
        console.error('Error fetching booking history:', data.message);
        alert('Failed to fetch booking history');
      }
    } catch (error) {
      console.error('Error fetching booking history:', error);
      alert('Failed to fetch booking history');
    } finally {
      setBookingHistoryLoading(false);
    }
  };

  // Close booking history modal
  const closeBookingHistoryModal = () => {
    setBookingHistoryOpen(false);
    setBookingHistoryUser(null);
    setBookingHistory([]);
    setBookingHistoryPagination({ page: 1, limit: 10, total: 0, totalPages: 0 });
  };

  // Initial fetch and when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, sortField, sortOrder]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
      fetchUsers();
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Reset all filters
  const resetFilters = () => {
    setSearch('');
    setSortField('createdAt');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return <BiSortAlt2 className="ml-1" />;
    return sortOrder === 'asc' ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />;
  };

  // Calculate pages for pagination
  const pagesToShow = [];
  const maxPagesToShow = 5;
  
  if (totalPages <= maxPagesToShow) {
    for (let i = 1; i <= totalPages; i++) {
      pagesToShow.push(i);
    }
  } else {
    const leftSide = Math.floor(maxPagesToShow / 2);
    const rightSide = maxPagesToShow - leftSide;

    if (currentPage <= leftSide) {
      for (let i = 1; i <= maxPagesToShow; i++) {
        pagesToShow.push(i);
      }
    } else if (currentPage >= totalPages - rightSide) {
      for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
        pagesToShow.push(i);
      }
    } else {
      for (let i = currentPage - leftSide; i <= currentPage + rightSide; i++) {
        pagesToShow.push(i);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl shadow-card p-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">User Management</h1>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
            {/* Export Button */}
            <button
              onClick={exportToExcel}
              disabled={exportLoading || users.length === 0}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium ${
                exportLoading || users.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {exportLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <FiDownload className="mr-2" />
                  Export Excel ({totalUsers})
                </>
              )}
            </button>
            
            {/* Search */}
            <div className="w-full sm:w-64 relative">
              <input
                type="text"
                placeholder="Search by name, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <FiFilter className="mr-1" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={resetFilters}
                className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                <FiRefreshCw className="mr-1" /> Reset
              </button>
            </div>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg mt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search by Date Range</label>
                <input
                  type="date"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Filter by date..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-100 border-b">               
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('name')}>
                  <div className="flex items-center">
                    Name {getSortIcon('name')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('phoneNumber')}>
                  <div className="flex items-center">
                    Phone {getSortIcon('phoneNumber')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('createdAt')}>
                  <div className="flex items-center">
                    Created {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                      <span>Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">                   
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{user.email || 'No email'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.phoneNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => viewUserDetails(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FiEye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => fetchUserBookingHistory(user)}
                          className="text-purple-600 hover:text-purple-900"
                          title="View Bookings"
                        >
                          <FiBook className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {users.length > 0 && (
          <div className="flex flex-col md:flex-row justify-between items-center mt-6">
            <div className="text-sm text-gray-700 mb-4 md:mb-0">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md ${
                  currentPage === 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                First
              </button>
              
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md ${
                  currentPage === 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Prev
              </button>
              
              {pagesToShow.map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md ${
                  currentPage === totalPages 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
              
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md ${
                  currentPage === totalPages 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Last
              </button>
            </div>
            
            <div className="mt-4 md:mt-0 flex items-center space-x-2">
              <span className="text-sm text-gray-700">Items per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-md p-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {userDetailsModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl w-[95%] max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center border-b pb-5 mb-6">
                <h2 className="text-3xl font-bold text-gray-800">User Details</h2>
                <button
                  onClick={closeUserDetailsModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX className="h-7 w-7" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* User Profile Information */}
                <div className="md:col-span-1 flex flex-col items-center">                  
                  <h3 className="text-2xl font-semibold text-center mb-1">{selectedUser.name || 'N/A'}</h3>
                  <p className="text-lg text-gray-600 mb-6">{selectedUser.phoneNumber}</p>
                </div>

                {/* User Details */}
                <div className="md:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Contact Information */}
                    <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-xl mb-4 border-b pb-3 text-gray-800">Contact Information</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="text-base text-gray-500 mb-1">Phone Number</p>
                          {editingPhone ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={newPhoneNumber}
                                onChange={(e) => setNewPhoneNumber(e.target.value)}
                                className="text-lg font-medium border border-gray-300 rounded px-2 py-1 w-full focus:outline-none focus:border-blue-500"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUpdatePhoneNumber();
                                  if (e.key === 'Escape') { setEditingPhone(false); setNewPhoneNumber(''); }
                                }}
                              />
                              <button
                                onClick={handleUpdatePhoneNumber}
                                disabled={phoneUpdateLoading}
                                className="text-green-600 hover:text-green-800 disabled:opacity-50"
                                title="Save"
                              >
                                {phoneUpdateLoading ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                                ) : (
                                  <FiCheck className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => { setEditingPhone(false); setNewPhoneNumber(''); }}
                                className="text-red-500 hover:text-red-700"
                                title="Cancel"
                              >
                                <FiX className="h-5 w-5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <p className="text-lg font-medium">{selectedUser.phoneNumber}</p>
                              <button
                                onClick={() => { setEditingPhone(true); setNewPhoneNumber(selectedUser.phoneNumber); }}
                                className="text-gray-400 hover:text-blue-600"
                                title="Edit phone number"
                              >
                                <FiEdit2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>

                        <div>
                          <p className="text-base text-gray-500 mb-1">Email</p>
                          <p className="text-lg font-medium">{selectedUser.email || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Account Information */}
                    <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-xl mb-4 border-b pb-3 text-gray-800">Account Information</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="text-base text-gray-500 mb-1">User ID</p>
                          <p className="text-sm font-mono bg-gray-200 p-2 rounded">{selectedUser._id}</p>
                        </div>
                        
                        <div>
                          <p className="text-base text-gray-500 mb-1">Created At</p>
                          <p className="text-lg font-medium">{formatDate(selectedUser.createdAt)}</p>
                        </div>
                        
                        <div>
                          <p className="text-base text-gray-500 mb-1">Last Updated</p>
                          <p className="text-lg font-medium">{formatDate(selectedUser.updatedAt)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Activity Statistics */}
                  {statsLoading ? (
                    <div className="mt-6 bg-blue-50 p-8 rounded-lg shadow-sm border border-blue-200 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                      <p className="text-blue-600">Loading statistics...</p>
                    </div>
                  ) : userDetailsWithStats ? (
                    <div className="mt-6 bg-blue-50 p-5 rounded-lg shadow-sm border border-blue-200">
                      <h4 className="font-semibold text-xl mb-4 border-b border-blue-200 pb-3 text-blue-800">Activity Summary</h4>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {userDetailsWithStats.statistics?.totalBookings || 0}
                          </div>
                          <div className="text-sm text-gray-600">Total Bookings</div>
                        </div>
                        
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            ₹{userDetailsWithStats.statistics?.totalSpent || 0}
                          </div>
                          <div className="text-sm text-gray-600">Total Spent</div>
                        </div>
                        
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">
                            {userDetailsWithStats.statistics?.totalReviews || 0}
                          </div>
                          <div className="text-sm text-gray-600">Reviews Given</div>
                        </div>
                        
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">
                            {userDetailsWithStats.statistics?.avgRatingGiven ? 
                              userDetailsWithStats.statistics.avgRatingGiven.toFixed(1) : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">Avg Rating Given</div>
                        </div>
                      </div>

                      {/* Booking Status Breakdown */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-2 bg-white rounded-lg">
                          <div className="text-lg font-bold text-yellow-600">
                            {userDetailsWithStats.statistics?.pendingBookings || 0}
                          </div>
                          <div className="text-xs text-gray-600">Pending</div>
                        </div>
                        
                        <div className="text-center p-2 bg-white rounded-lg">
                          <div className="text-lg font-bold text-blue-600">
                            {userDetailsWithStats.statistics?.confirmedBookings || 0}
                          </div>
                          <div className="text-xs text-gray-600">Confirmed</div>
                        </div>
                        
                        <div className="text-center p-2 bg-white rounded-lg">
                          <div className="text-lg font-bold text-green-600">
                            {userDetailsWithStats.statistics?.completedBookings || 0}
                          </div>
                          <div className="text-xs text-gray-600">Completed</div>
                        </div>
                        
                        <div className="text-center p-2 bg-white rounded-lg">
                          <div className="text-lg font-bold text-red-600">
                            {userDetailsWithStats.statistics?.cancelledBookings || 0}
                          </div>
                          <div className="text-xs text-gray-600">Cancelled</div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Recent Bookings */}
                  {userDetailsWithStats?.recentBookings && userDetailsWithStats.recentBookings.length > 0 && (
                    <div className="mt-6 bg-gray-50 p-5 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-xl mb-4 border-b pb-3 text-gray-800">Recent Bookings</h4>
                      <div className="space-y-3">
                        {userDetailsWithStats.recentBookings.map((booking) => (
                          <div key={booking._id} className="flex justify-between items-center p-3 bg-white rounded-lg">
                            <div>
                              <p className="font-medium">{booking.vendorId?.store_name || booking.vendorId?.name}</p>
                              <p className="text-sm text-gray-500">
                                {booking.bookingDate} at {booking.timeSlot}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">₹{booking.totalAmount}</p>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                                booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {booking.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View All Bookings Button */}
                  {userDetailsWithStats && (
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={() => {
                          const user = selectedUser;
                          closeUserDetailsModal();
                          fetchUserBookingHistory(user);
                        }}
                        className="px-5 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 flex items-center gap-2"
                      >
                        <FiBook className="h-4 w-4" />
                        View All Bookings
                      </button>
                    </div>
                  )}

                  {/* Recent Reviews */}
                  {userDetailsWithStats?.recentReviews && userDetailsWithStats.recentReviews.length > 0 && (
                    <div className="mt-6 bg-gray-50 p-5 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-xl mb-4 border-b pb-3 text-gray-800">Recent Reviews</h4>
                      <div className="space-y-3">
                        {userDetailsWithStats.recentReviews.map((review) => (
                          <div key={review._id} className="p-3 bg-white rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <p className="font-medium">{review.vendorId?.store_name || review.vendorId?.name}</p>
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <svg key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                                  </svg>
                                ))}
                              </div>
                            </div>
                            {review.comment && (
                              <p className="text-sm text-gray-600">{review.comment}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-2">{formatDate(review.createdAt)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>                          
            </div>
          </div>
        </div>
      )}
      {/* Booking History Modal */}
      {bookingHistoryOpen && bookingHistoryUser && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl w-[95%] max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center border-b pb-5 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Booking History</h2>
                  <p className="text-gray-500 mt-1">
                    {bookingHistoryUser.name || 'User'} - {bookingHistoryUser.phoneNumber}
                  </p>
                </div>
                <button
                  onClick={closeBookingHistoryModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX className="h-7 w-7" />
                </button>
              </div>

              {bookingHistoryLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading bookings...</span>
                </div>
              ) : bookingHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FiCalendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg">No bookings found for this user</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Services</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {bookingHistory.map((booking) => (
                          <tr key={booking._id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {booking.vendorId?.store_name || booking.vendorId?.name || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {booking.vendorId?.vendorType === 'home' ? 'Home Service' : 'In-store'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {moment(booking.bookingDate).format('MMM D, YYYY')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {moment(booking.timeSlot, ['h:mm A', 'hh:mm A', 'HH:mm'], true).format('h:mm A')}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-900">
                                {booking.selectedServices && booking.selectedServices.length > 0 ? (
                                  <>
                                    {booking.selectedServices.slice(0, 2).map((s, i) => (
                                      <div key={i} className="text-sm">{s.name}</div>
                                    ))}
                                    {booking.selectedServices.length > 2 && (
                                      <span className="text-xs text-gray-500">+{booking.selectedServices.length - 2} more</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-gray-400 text-xs">No services</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                              ₹{booking.totalAmount}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
                                booking.status === 'completed' ? 'bg-green-100 text-green-800 border-green-200' :
                                booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-800 border-red-200' :
                                'bg-yellow-100 text-yellow-800 border-yellow-200'
                              }`}>
                                {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : 'Unknown'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
                                booking.paymentStatus === 'paid' ? 'bg-green-100 text-green-800 border-green-200' :
                                booking.paymentStatus === 'refunded' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                                booking.paymentStatus === 'failed' ? 'bg-red-100 text-red-800 border-red-200' :
                                'bg-yellow-100 text-yellow-800 border-yellow-200'
                              }`}>
                                {booking.paymentStatus ? booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1) : 'Unknown'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Booking History Pagination */}
                  {bookingHistoryPagination.totalPages > 1 && (
                    <div className="flex justify-between items-center mt-4 pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Showing {((bookingHistoryPagination.page - 1) * bookingHistoryPagination.limit) + 1} to{' '}
                        {Math.min(bookingHistoryPagination.page * bookingHistoryPagination.limit, bookingHistoryPagination.total)} of{' '}
                        {bookingHistoryPagination.total} bookings
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchUserBookingHistory(bookingHistoryUser, bookingHistoryPagination.page - 1)}
                          disabled={bookingHistoryPagination.page === 1}
                          className={`px-3 py-1 rounded-md text-sm ${
                            bookingHistoryPagination.page === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                          Page {bookingHistoryPagination.page} of {bookingHistoryPagination.totalPages}
                        </span>
                        <button
                          onClick={() => fetchUserBookingHistory(bookingHistoryUser, bookingHistoryPagination.page + 1)}
                          disabled={bookingHistoryPagination.page === bookingHistoryPagination.totalPages}
                          className={`px-3 py-1 rounded-md text-sm ${
                            bookingHistoryPagination.page === bookingHistoryPagination.totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailTable;